'use client';

import React from 'react';

import { Button } from '@/components/ui/button';

import { cn } from '@/lib/utils';

import { ONBOARDING_SCREENS } from '../../constants/user-onboarding-constants';
import { useUserOnboarding } from '../../contexts/user-onboarding-provider';

type OnboardingFooterProps = {
	onNext?: () => void;
	onPrevious?: () => void;
	nextLabel?: string;
	previousLabel?: string;
	showPrevious?: boolean;
	showNext?: boolean;
	nextDisabled?: boolean;
	className?: string;
};

export function OnboardingFooter({
	onNext,
	onPrevious,
	nextLabel = 'Continue',
	previousLabel = 'Back',
	showPrevious = true,
	showNext = true,
	nextDisabled = false,
	className,
}: OnboardingFooterProps) {
	const { currentScreenIndex, goToNextScreen, goToPreviousScreen, completeOnboarding } =
		useUserOnboarding();

	const isFirstScreen = currentScreenIndex === 0;
	const isLastScreen = currentScreenIndex === ONBOARDING_SCREENS.length - 1;

	const handleNext = () => {
		if (onNext) {
			onNext();
		} else if (isLastScreen) {
			completeOnboarding();
		} else {
			goToNextScreen();
		}
	};

	const handlePrevious = () => {
		if (onPrevious) {
			onPrevious();
		} else {
			goToPreviousScreen();
		}
	};

	const isNextDisabled = nextDisabled;

	return (
		<div className={cn('flex items-center justify-between', className)}>
			{/* Previous Button */}
			{showPrevious && !isFirstScreen && (
				<Button
					variant="outline"
					onClick={handlePrevious}
					className="min-w-[100px]"
				>
					{previousLabel}
				</Button>
			)}

			{/* Spacer for centering when no previous button */}
			{(!showPrevious || isFirstScreen) && <div />}

			{/* Next/Complete Button */}
			{showNext && (
				<Button
					onClick={handleNext}
					disabled={isNextDisabled}
					className="min-w-[100px]"
				>
					{isLastScreen ? 'Complete Setup' : nextLabel}
				</Button>
			)}
		</div>
	);
}
